const CHECKOUT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

export function loadRazorpayScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('Browser only'));
  if (window.Razorpay) return Promise.resolve();

  const existing = document.querySelector(`script[src="${CHECKOUT_URL}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Razorpay script failed')));
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = CHECKOUT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not load Razorpay checkout'));
    document.body.appendChild(script);
  });
}

/**
 * Opens Razorpay Checkout. Resolves with { razorpay_order_id, razorpay_payment_id, razorpay_signature }.
 * On transient bank failure that may later authorize, rejects with error.paymentId / error.orderId
 * so the app can poll /api/payment/recover.
 */
export function isRazorpayTestKey(keyId) {
  return typeof keyId === 'string' && keyId.startsWith('rzp_test_');
}

export async function openRazorpayCheckout({ keyId, orderId, amount, currency, name, email, description }) {
  await loadRazorpayScript();

  const amountPaise = Math.round(Number(amount));

  return new Promise((resolve, reject) => {
    const options = {
      key: keyId,
      amount: amountPaise,
      currency: currency || 'INR',
      name: name || 'Repatikosam',
      description: description || 'Annual subscription (1 year)',
      order_id: orderId,
      prefill: {
        email: email || '',
        name: name || '',
      },
      theme: { color: '#1a6b2f' },
      retry: { enabled: true, max_count: 3 },
      handler(response) {
        resolve(response);
      },
      modal: {
        confirm_close: true,
        ondismiss() {
          const err = new Error('Payment cancelled');
          err.code = 'cancelled';
          err.orderId = orderId;
          reject(err);
        },
      },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        const msg = resp.error?.description || resp.error?.reason || 'Payment failed';
        const err = new Error(msg);
        err.code = 'payment_failed';
        err.orderId = orderId;
        err.paymentId = resp.error?.metadata?.payment_id || resp.error?.payment_id || '';
        // Keep checkout open for retries — only reject if user will close.
        // Still expose ids so caller can recover if bank later authorizes.
        reject(err);
      });
      rzp.open();
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * Poll server until Razorpay shows authorized/captured for this order (late bank success).
 */
export async function pollPaymentRecover({ apiBase, token, orderId, paymentId, attempts = 12, intervalMs = 4000 }) {
  let last = null;
  for (let i = 0; i < attempts; i += 1) {
    try {
      const res = await fetch(`${apiBase}/api/payment/recover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ orderId, paymentId }),
      });
      const data = await res.json().catch(() => ({}));
      last = data;
      if (res.ok && data.success && data.isSubscribed) return data;
    } catch {
      /* keep trying */
    }
    if (i < attempts - 1) {
      await new Promise((r) => setTimeout(r, intervalMs));
    }
  }
  return last;
}
